import { Injectable } from '@angular/core';
import {  Observable, Subject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { HttpHeadersService } from '../../core/shared-service/http-headers.service';
import * as UpChunk from '@mux/upchunk';

import {
    ImageInfoExt,
    ImageInfoRaw,
    ImageSize,
    VideoInfoExt,
    VideoInfoRaw,
    VideoLibrary,
    VideoLibraryExt
} from './video-library.model';
import { CommonUtil } from 'src/app/core/Utils/common-util';
import { AmplitudeAnalyticsService } from 'src/app/core/services/amplitude-analytics.service';
import { FacilityService } from 'src/app/core/services/facility.service';
import { DatePipe } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class VideoLibraryService {
    private tokenInfo: any;
    private getTokenPromise: Promise<any> | undefined;
    private _videoLibrary: VideoLibraryExt = {totalCount: 0, videoList: [], countExceeded: false, minutesExceeded: false};
    private _uploadingVideoList: VideoInfoRaw[] = [];
    private _uploadingVideoListBs = new Subject<VideoInfoRaw[]>();
    private cancelPromise: Promise<any> | undefined;
    private currentUserId: any;

    uploadingVideoListSubscriber = this._uploadingVideoListBs.asObservable();
    
    baseTypes = [
        {value: 'Treadmill', transId: 'TREADMILL'},
        {value: 'Cross_Trainer', transId: 'CROSS.TRAINER'},
        {value: 'Recumbent_Bike', transId: 'RECUMBENT.BIKE'},
        {value: 'Upright_Bike', transId: 'UPRIGHT.BIKE'},
        {value: 'Flex_Strider', transId: 'FLEXSTRIDER'},
        {value: 'Arc_Trainer', transId: 'ARC.TRAINER'},
        {value: 'Powermill', transId: 'POWERMILL'},
        {value: 'GSC_Trainer', transId: 'GSC.TRAINER'}];
    constructor(
        private _http: HttpClient,
        private _httpHeaderService: HttpHeadersService,  
        private commonUtil: CommonUtil,
        private _amplitudeService: AmplitudeAnalyticsService,
        private facilityService: FacilityService
    ) {
        this.cancelPromise = this.handleUploadCancelled();        
    }

    async getVideoLibraryList(facilityId:string, pageNumber:string, itemsPerPage: string, sortBy:string, sortType:string){
        if(this.cancelPromise) {
            try {
                await this.cancelPromise;
            } catch (error) {
                console.error('API failed.');
            }
        }
        let headers = await this.getHeader();
        headers = headers.set('Content-Type', 'application/json');
        headers = headers.set('Accept', 'application/json');
        this._videoLibrary =  await this._http.get(`${environment.apigeeCMSUrl}/videos?facilityId=${facilityId}&pageNumber=${pageNumber}&videosPerPage=${itemsPerPage}&sortBy=${sortBy}&sortType=${sortType}&apikey=${environment.apiKey}&browserTime=${this.getBrowserTime()}`, { headers }).toPromise() as VideoLibrary;
        this._videoLibrary?.videoList?.forEach((v:any) => {
            if(v.muxAsset) v.attributes = { muxAsset: v.muxAsset, status: v.status }
        });
        if(this._videoLibrary) {
            this._videoLibrary.allowUpload = true;
            const videoAddList: VideoInfoRaw[] = [];                
            this._uploadingVideoList.forEach((v,i,a) => {
                const facId = v.attributes?.muxAsset?.data?.attributes.metadata?.data?.attributes.initUploadFacility
                            ?? v.attributes?.facilityId;
                if(facId === facilityId){
                    let isUpdated = false;
                    const idx = this._videoLibrary.videoList?.findIndex(u => v.attributes?.muxAsset?.data?.id===u.attributes?.muxAsset?.data?.id);
                    if(idx != undefined && idx > -1) {
                        this._videoLibrary.videoList[idx].attributes.uploadProgress = v.attributes.uploadProgress;
                        this._videoLibrary.videoList[idx].attributes.facilityId = v.attributes.facilityId;
                        this._videoLibrary.videoList[idx].attributes.muxUpChunkInstance = v.attributes.muxUpChunkInstance;
                        this._uploadingVideoList[i] = this._videoLibrary.videoList[idx];
                        isUpdated = true;
                    }
                    else {
                        videoAddList.push(v)
                    }
                    this._videoLibrary.allowUpload = false;
                    if(isUpdated) this.setUploadingVideosLocalStorage();
                }                                    
            });
            if(videoAddList.length > 0){
                this._videoLibrary.videoList = [...videoAddList, ...this._videoLibrary.videoList]
            }
            this._videoLibrary.videoList?.forEach(v => {                    
                const type = this.baseTypes.find(b => b.value === v.attributes.baseType);
                if(type) v.attributes.baseTypeDisplayName = this.commonUtil.translateText(type.transId);
                v.attributes.displayWorkoutImage = v.attributes.workoutThumbnail?.data?.attributes;
                if(!v.attributes.displayWorkoutImage) v.attributes.displayWorkoutImage = v.attributes.muxAsset?.data?.attributes.metadata?.data?.attributes.defaultThumbnail?.data?.attributes;
            });
        }
        if(this._videoLibrary.totalCount === 0) this._videoLibrary.allowUpload = false;
        return this._videoLibrary;
    }
    
    getBrowserTime() {
        const today = new Date();
        let date = new DatePipe('en-Us').transform(today, 'yyyy-MM-ddThh:mm:ssZZZZZ');
        if(date) {
            date = encodeURIComponent(date);
        }
        return date;
    }
    async deleteVideoLibrary(payload:any) {
        let headers = await this.getHeader();
        headers = headers.set('Content-Type', 'application/json');
        headers = headers.set('Accept', 'application/json');
        const options = {
            headers: headers,
            body: payload
          };
        return this._http.delete(`${environment.apigeeCMSUrl}/videoDetails?apikey=${environment.apiKey}`, options).toPromise();
    }

    async copyVideoLibrary(payload:any) {
        const headers = await this.getHeader();
        return this._http.post(`${environment.apigeeCMSUrl}/videoDetails/copy?apikey=${environment.apiKey}`, payload, {headers}).toPromise();
    }

    async getUploadVideoUrl(payload:any){
        let headers = await this.getHeader();
        headers = headers.set('Content-Type', 'application/json');
        headers=headers.set('Accept', 'application/json');
        return this._http.post(`${environment.apigeeCMSUrl}/uploadURL?apikey=${environment.apiKey}`, payload, {headers}).toPromise();
    }

    async addVideoInfo(data: any) {
        const headers = await this.getHeader();
        return this._http.post(`${environment.apigeeCMSUrl}/videoDetails?populate=*&apikey=${environment.apiKey}`, data, {headers}).toPromise();
    }

    async editVideoInfo(data: any, id: number) {
        const headers = await this.getHeader();
        return this._http.put(`${environment.apigeeCMSUrl}/videoDetails/${id}?populate=*&apikey=${environment.apiKey}`, data, {headers}).toPromise();
    }

    async getVideoInfo(id: number) {
        const idx = this._videoLibrary?.videoList?.findIndex(m => m.attributes.muxAsset?.data?.id == id);
        if(idx > -1 && this._videoLibrary.videoList[idx].id){
            try {
                const headers = await this.getHeader();
                const res: any = await this._http.get(`${environment.apigeeCMSUrl}/videoDetails/${this._videoLibrary.videoList[idx].id}?apikey=${environment.apiKey}&populate[instructor][populate]=*&populate[workoutThumbnail]=*&populate[muxAsset][populate][metadata][populate]=*`, {headers}).toPromise();
                this._videoLibrary.videoList[idx] = res?.data;
            } catch (error) {
                console.error('API failed.')
            }
        }
        return this._videoLibrary.videoList[idx];
    }

    setUploadingVideoList(videoList: VideoInfoRaw[]) {
        videoList.forEach(val => {
            this._uploadingVideoList.push(val);
        })        
        if(this._videoLibrary.videoList?.length === 0) {
            this._videoLibrary.videoList = videoList;
        }
        this._videoLibrary.allowUpload = false;
    }

    getUploadingVideoList(){
        return this._uploadingVideoList;
    }
    
    timeout(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    getEnabledOnDemandClasses(facilityIds: any) {
        const headers = this._httpHeaderService.bearerHttpAuthorizationHeader();
        const result = this._http.get(`${environment.gcpServicePath}/enable_equipment/enabled_count?facilityIds=${facilityIds}`, { headers });
        return result as Observable<any>;
    }

    getEnabledEquipment(facilityIds: number[], consoleType: string) {
        const headers = this._httpHeaderService.bearerHttpAuthorizationHeader();
        const data = {
            consoleType: consoleType,
            facilityIds: facilityIds
        }
        const result = this._http.post(`${environment.gcpServicePath}/enable_equipment/get`, data, { headers });
        return result as Observable<any>;
    }

    async publishCustomUploadedVideos(payload:any){
        let headers = await this.getHeader();
        headers = headers.set('Content-Type', 'application/json');
        headers=headers.set('Accept', 'application/json');
        return this._http.post(`${environment.apigeeCMSUrl}/videos/publish?apikey=${environment.apiKey}`, payload, {headers}).toPromise();
    }

    async unpublishCustomUploadedVideos(payload:any) {
        let headers = await this.getHeader();
        headers = headers.set('Content-Type', 'application/json');
        headers=headers.set('Accept', 'application/json');
        return this._http.post(`${environment.apigeeCMSUrl}/videos/unpublish?apikey=${environment.apiKey}`, payload, {headers}).toPromise();
    }

    //Below is the Observable function to upload MUX vdo file.
    uploadVideoLibrary(videoInfo: VideoInfoRaw, url: string){
        if(!videoInfo?.attributes.file) return;
        this.setUploadingVideosLocalStorage();
        const upload = UpChunk.createUpload({
            endpoint: url,
            file: videoInfo.attributes.file,
            chunkSize: 5120,// Uploads the file in ~5mb chunks
        });
        videoInfo.attributes.muxUpChunkInstance = upload;
        // subscribe to events
        upload.on('error', err => {
            console.error('-error upload video--', err.detail);
            const data = this._uploadingVideoList.find(u => u.attributes.muxAsset?.data?.id === videoInfo.attributes.muxAsset?.data?.id);
            if(data) {
                data.statusCode = -1;
                data.attributes.muxUpChunkInstance.abort();
            }
        });
        upload.on('offline', err =>{
            console.error('-error upload video, offline--', err);
            const data = this._uploadingVideoList.find(u => u.attributes.muxAsset?.data?.id === videoInfo.attributes.muxAsset?.data?.id);
            if(data) {
                data.statusCode = -1;
                data.attributes.muxUpChunkInstance.abort();
            }
        })
        upload.on('attemptFailure', err =>{
            console.error('-error upload video, attemptFailure--', err);
            const data = this._uploadingVideoList.find(u => u.attributes.muxAsset?.data?.id === videoInfo.attributes.muxAsset?.data?.id);
            if(data) {
                data.statusCode = -1;
                data.attributes.muxUpChunkInstance.abort();
            }
        })
        upload.on('progress', progress => {        
            const data = this._uploadingVideoList.find(u => u.attributes.muxAsset?.data?.id === videoInfo.attributes.muxAsset?.data?.id);
            if(data) data.attributes.uploadProgress = Math.round(progress.detail);
        });

        upload.on('success', () => {
            const idx = this._uploadingVideoList.findIndex(u => u.attributes.muxAsset?.data?.id === videoInfo.attributes.muxAsset?.data?.id);
            if(idx > -1) {
                this._uploadingVideoList.splice(idx, 1);
                this.setUploadingVideosLocalStorage();
                this._uploadingVideoListBs.next(this._uploadingVideoList);
            }
        });
    }

    async dismissUploadVideo(item: VideoInfoRaw){
        //Dismiss current uploading process by upchunk.abord.
        this.abordMuxUpChunkInstance(item);
        //Delete previous halfway uploaded chunk from mux and strapi.
        this.removeVideoFromList(item);
    }

    abordMuxUpChunkInstance(item: VideoInfoRaw){
        const upload = item.attributes.muxUpChunkInstance as UpChunk.UpChunk;
        upload?.abort();
    }

    async deletePreviousUploadedChunk(item: VideoInfoExt){
        let headers = await this.getHeader();
        headers = headers.set('Content-Type', 'application/json');
        headers = headers.set('Accept', 'application/json');
        return this._http.delete(`${environment.apigeeCMSUrl}/video/${item.muxAsset?.data?.id}?apikey=${environment.apiKey}`, {headers}).toPromise();
    }
    
    removeVideoFromList(item: VideoInfoRaw){
        let idx = this._videoLibrary.videoList.findIndex(u => u.attributes.muxAsset?.data?.id === item.attributes.muxAsset?.data?.id);
        if(idx !== -1) this._videoLibrary.videoList.splice(idx, 1);
        idx = this._uploadingVideoList.findIndex(u => u.attributes.muxAsset?.data?.id === item.attributes.muxAsset?.data?.id);
        if(idx !== -1) {
            this._uploadingVideoList.splice(idx, 1);
            this.setUploadingVideosLocalStorage();
        }
        if(this._videoLibrary.totalCount === 0) this._videoLibrary.allowUpload = false;
        else this._videoLibrary.allowUpload = !this._videoLibrary.videoList?.some(v => v.attributes.muxUpChunkInstance || (v.attributes.validationErrors && v.attributes.validationErrors?.length > 0));
    }

    async getInstructorList(facilityId: number) {
        const headers = await this.getHeader();
        return this._http.get(`${environment.apigeeCMSUrl}/custom/instructor?filters[facilities][facilityId][$eq]=${facilityId}&populate=*&apikey=${environment.apiKey}`, {headers}).toPromise();   
    }

    async addInstructor(payload: any) {
        const headers = await this.getHeader();
        return this._http.post(`${environment.apigeeCMSUrl}/custom/instructor?apikey=${environment.apiKey}&populate=*`, payload, {headers}).toPromise();
    }

    async updateInstructor(payload: any,id: number) {
        const headers = await this.getHeader();
        return this._http.put(`${environment.apigeeCMSUrl}/custom/instructor/${id}?apikey=${environment.apiKey}&populate=*`, payload, {headers}).toPromise();
    }

    setUploadingVideosLocalStorage() {
        const list = this._uploadingVideoList.map(v => {return {muxId: v.attributes.muxAsset?.data?.id, facilityId: v.attributes.facilityId, id: v.id}})
        localStorage.setItem('uploadingVideos', JSON.stringify(list));
    }

    getAvailableImageFormat(imageInfo: ImageInfoRaw | undefined, size: ImageSize, donotTakeSmaller = false): ImageInfoExt | undefined{
        if(!imageInfo?.data?.attributes) return undefined;
        return this.getAvailableImageFormatExt(imageInfo, size, size, donotTakeSmaller);
    }
    
    private getAvailableImageFormatExt(imageInfo: ImageInfoRaw, size: ImageSize, requestedSize: ImageSize, donotTakeSmaller = false): ImageInfoExt | undefined{        
        let img: ImageInfoExt | undefined;
        if(imageInfo?.data?.attributes?.formats) {
            switch(size){
                case ImageSize.THUMBNAIL:
                    img = imageInfo.data?.attributes.formats.thumbnail;
                    break;
                case ImageSize.SMALL:
                    img = imageInfo.data?.attributes.formats.small;
                    break;
                case ImageSize.MEDIUM:
                    img = imageInfo.data?.attributes.formats.medium;
                    break;
                case ImageSize.LARGE:
                    img = imageInfo.data?.attributes.formats.large;
                    break;
            }
        }
        if(!img){
            if(size === ImageSize.THUMBNAIL || donotTakeSmaller) img = imageInfo?.data?.attributes;
            else if(requestedSize > ImageSize.THUMBNAIL) img = this.getAvailableImageFormatExt(imageInfo, size - 1, requestedSize, donotTakeSmaller);
        } 
        return img;
    }

    async getHeader() {
        await this.getGcpToken();
        let headers = new HttpHeaders();
        headers = headers.set('Authorization', "Bearer " + this.tokenInfo.token);
        return headers;
    }

    private async getGcpToken() {
        try {
          if(this.getTokenPromise) await this.getTokenPromise;
          else {
            if(this.tokenInfo?.expiresAt > Date.now() && 
                this.currentUserId === this.commonUtil.rootScopeData.currentUser.userId) return;
            this.currentUserId = this.commonUtil.rootScopeData.currentUser.userId;
            const headers = this._httpHeaderService.bearerHttpAuthorizationHeader();
            this.getTokenPromise = this._http.get(`${environment.servicePath}/maintenance/jwtToken?userId=${this.currentUserId}&facilityId=${this.facilityService.selectedFacilityId()}&product=video_library`, { headers }).toPromise();
            this.tokenInfo = await this.getTokenPromise;
            this.getTokenPromise = undefined;
            if (Number.isNaN(this.tokenInfo.expiresAt)) this.tokenInfo.expiresAt = Number.parseInt(this.tokenInfo.expiresAt.toString());
          }
        } catch (error) {
          this.getTokenPromise = undefined;
          throw error;
        }
    }

    private async handleUploadCancelled() {
        const uploadingVideosListRow :string | null = localStorage.getItem('uploadingVideos');
        if(uploadingVideosListRow) {
            await this.facilityService.get(); // wait until facility information is retrieved
            const uploadingVideos: {id: string, muxId: string, facilityId: string} [] = JSON.parse(uploadingVideosListRow);        
            this._amplitudeService.logEvent({elementName: 'Delete Uploading Videos', action: "Refresh App"});
            for (const item of uploadingVideos) {
                if(item.muxId) {
                    const payload = {
                        "facilityIds": [Number(item.facilityId)],
                        "strapiId-MuxId": [`${(item.id?item.id:0)}-${item.muxId}`]
                    }
                    await this.deleteVideoLibrary(payload);
                }
            }
            localStorage.setItem('uploadingVideos', '');
        }
    }
}
